import React from 'react'
import PropTypes from 'prop-types'
import { Alert, Button } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'

import SettingsCard from '../../../components/Settings/SettingsCard'
import SettingsLayout from '../../../components/Settings/SettingsLayout'
import { ROUTES } from '../../../constants/routes'
import SocialMediaCard from '../../account/settings/SocialMediaCard'
import CharityStripeConnectSection from '../dashboard/stripe-connect-section'
import CharityDonationLinksCard from './CharityDonationLinksCard'
import CharityLocationsCard from './CharityLocationsCard'
import CharityLogoCard from './CharityLogoCard'
import CharityOrganizationCard from './CharityOrganizationCard'
import { CHARITY_SOCIAL_FIELDS } from './constants'

export default function CharitySettingsContent({
  charity,
  charityId,
  breadcrumbs,
  error,
  setError,
  stripeMessage,
  setStripeMessage,
  verifiedCharities,
  locations,
  clearedLocations,
  onLocationChange,
  updateCharityField,
  updateSocialField,
  onLogoChange,
  onSaveDonationLinks,
  onSaveSocials,
  onSaveLocations,
  hasDonationLinksChanged,
  hasSocialsChanged,
  hasLocationsChanged,
  saving,
  stripeReturnPath,
  t,
}) {
  const navigate = useNavigate()

  return (
    <SettingsLayout
      title={t('charities.settings.title')}
      breadcrumbs={breadcrumbs}
    >
      {verifiedCharities.length > 1 ? (
        <div className="mb-3 d-flex justify-content-end">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate(ROUTES.CHARITY_ACCOUNT)}
          >
            {t('charities.settings.switch_charity')}
          </Button>
        </div>
      ) : null}

      {stripeMessage && (
        <Alert
          variant={stripeMessage.type}
          dismissible
          onClose={() => setStripeMessage(null)}
          className="mb-3"
        >
          {stripeMessage.text}
        </Alert>
      )}
      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError(null)}
          className="mb-3"
        >
          {error}
        </Alert>
      )}

      <CharityOrganizationCard charity={charity} />

      <CharityLogoCard
        logoUrl={charity?.logo_url}
        onLogoChange={onLogoChange}
      />

      <CharityDonationLinksCard
        charity={charity}
        onFieldChange={updateCharityField}
        onSave={onSaveDonationLinks}
        hasChanged={hasDonationLinksChanged}
        isSaving={saving}
      />

      <SocialMediaCard
        data={charity}
        onFieldChange={updateSocialField}
        onSave={onSaveSocials}
        hasChanged={hasSocialsChanged}
        isSaving={saving}
        fields={CHARITY_SOCIAL_FIELDS}
        title={t('charities.settings.social_title')}
      />

      <CharityLocationsCard
        locations={locations}
        clearedLocations={clearedLocations}
        onLocationChange={onLocationChange}
        onSave={onSaveLocations}
        hasChanged={hasLocationsChanged}
        isSaving={saving}
      />

      <SettingsCard
        icon="building"
        title={t('charities.dashboard.stripe.title')}
      >
        <CharityStripeConnectSection
          charityId={charityId}
          returnPath={stripeReturnPath}
        />
      </SettingsCard>
    </SettingsLayout>
  )
}

CharitySettingsContent.propTypes = {
  charity: PropTypes.object.isRequired,
  charityId: PropTypes.number.isRequired,
  breadcrumbs: PropTypes.array.isRequired,
  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  stripeMessage: PropTypes.shape({
    type: PropTypes.string,
    text: PropTypes.string,
  }),
  setStripeMessage: PropTypes.func.isRequired,
  verifiedCharities: PropTypes.array,
  locations: PropTypes.object.isRequired,
  clearedLocations: PropTypes.object.isRequired,
  onLocationChange: PropTypes.func.isRequired,
  updateCharityField: PropTypes.func.isRequired,
  updateSocialField: PropTypes.func.isRequired,
  onLogoChange: PropTypes.func.isRequired,
  onSaveDonationLinks: PropTypes.func.isRequired,
  onSaveSocials: PropTypes.func.isRequired,
  onSaveLocations: PropTypes.func.isRequired,
  hasDonationLinksChanged: PropTypes.bool,
  hasSocialsChanged: PropTypes.bool,
  hasLocationsChanged: PropTypes.bool,
  saving: PropTypes.bool,
  stripeReturnPath: PropTypes.string,
  t: PropTypes.func.isRequired,
}
